import React from "react";
import { AutocompleteInputChangeReason } from "@mui/material";
// import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import AutocompleteField from "controls/global/custom-autocomplete";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { 
  useLookupActions, 
  useLookupIsSearchinggCompanyLocation 
} from "utils/context/LookupContext";

interface Props {
  label: string;
  placeholder?: string;
  name: string;
  /**
   * Receives a CompanyLocation when an option is picked, but a raw string
   * whenever the user commits free text (the underlying autocomplete runs with
   * freeSolo enabled, and blur forwards the typed text verbatim). Callers must
   * handle both.
   */
  onChange: (newLocation: CompanyLocation | string) => void;
  onBlur?: (event: React.ChangeEvent<any>) => void;
  onClear?: () => void;
  locationUniqueID?: string;
  size?: "small" | "medium";
  disabled?: boolean;
  excludeIds?: string[];
  /**
   * Display names to hide from the results. Use when the caller only knows the
   * location by name (no locationUniqueID). Matched case-insensitively against
   * locationDisplayName / locationFullDisplayName / ...WithStatus, since callers
   * may hold any of those three formats.
   */
  excludeDisplayNames?: string[];
  assignedLocationUniqueID?: string;
  agencyKey?: string;
  showAll?: boolean;
}

const AgencyLocationField = ({ label, placeholder, name, onChange, onBlur, onClear, locationUniqueID, size, disabled, excludeIds, excludeDisplayNames, assignedLocationUniqueID, agencyKey, showAll = false }: Props) => {
  // const { setValue } = useFormWrapper();
  const [, { searchCompanyLocations, searchSharingAgencyLocations }] = useLookupActions();
  const [{ isSearchingCompanyLocation }] = useLookupIsSearchinggCompanyLocation();

  // Please don't remove this
  // Notes: will be used if needed for styling dropdown content
  // const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CompanyLocation) => (
  //   <Typography component="li" {...props}>{option.locationFullDisplayName}</Typography>
  // );

  const onSearchCompanyLocations = async (
    searchText: string,
    locationUniqueID?: string,
  ): Promise<CompanyLocation[]> => {
    if (!searchText) return [];
    const results = assignedLocationUniqueID
      ? await searchSharingAgencyLocations(searchText, assignedLocationUniqueID, agencyKey, showAll)
      : await searchCompanyLocations(searchText, locationUniqueID, showAll);
    if (!excludeIds?.length && !excludeDisplayNames?.length) return results;
    const excludedNames = new Set(
      (excludeDisplayNames ?? [])
        .filter(Boolean)
        .map((n) => n.trim().toLowerCase())
    );
    // Hide agencies already selected in the other fields.
    return results.filter((option: CompanyLocation) => {
      if (excludeIds?.includes(option.locationUniqueID ?? "")) return false;
      if (!excludedNames.size) return true;
      return ![
        option.locationDisplayName,
        option.locationFullDisplayName,
        option.locationFullDisplayNameWithStatus,
      ].some((n) => n && excludedNames.has(n.trim().toLowerCase()));
    });
  };

  const onValueChanged = (newValue: CompanyLocation | string) => {
    onChange && onChange(newValue);
  };

  const handleOnBlur = (event: React.ChangeEvent<any>) => {
    onBlur && onBlur(event);
  };

  const getOptionLabel = (option: CompanyLocation | string) => {
    const label =
      typeof option === "string"
        ? option
        : option.locationFullDisplayNameWithStatus ||
          "";
    return label;
  };

  const getOptionSelected = (option: CompanyLocation, value: CompanyLocation) =>
    option.locationUniqueID === value.locationUniqueID;

  return (
    <AutocompleteField
      label={label}
      placeholder={placeholder}
      loading={isSearchingCompanyLocation}
      minLengthBeforeSearch={3}
      name={name}
      size={size}
      onTypeInput={(
        inputText: string,
        reason: AutocompleteInputChangeReason
      ) => {
        // if (reason !== "input") return;
        // setValue(name, inputText);
        if (reason.toString() === "clear") {
          onClear && onClear();
        }
      }}
      onGetOptions={(value:string) => onSearchCompanyLocations(value, locationUniqueID)}
      onValueChanged={onValueChanged}
      // renderOption={renderOption}    // please don't remove this
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      onBlur={handleOnBlur}
      disableClearable={false}
      disabled={disabled}
      popperPlacement="bottom"
      />
  );
};

export default AgencyLocationField;
